import { SceneService } from '@src/services/SceneService';

import { PunchListTrackManager } from '../lib/PunchListTrackManager';
import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { Scene } from '../models/layout';
import { AbstractElementCommand } from './AbstractCommand';
import { TimelineMode } from './TimelineMode';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command to move an element to a new position on the timeline
 * Uses direct object references to elements and tracks
 */
export class ShiftElementCommand extends AbstractElementCommand {
  private newStartTime: number;
  private newTrack?: number;
  private snapToFrame: boolean;

  getName(): string {
    return 'ShiftElementCommand';
  }
  private originalState: Record<string, any>;

  /**
   * Creates a new ShiftElementCommand
   * @param editor The video editor instance
   * @param element The element to move
   * @param newStartTime The new start time for the element (in seconds)
   * @param newTrack The new track for the element (optional)
   * @param snapToFrame Whether to snap the time to frame boundaries
   */
  constructor(
    editor: VideoEditor,
    element: Element,
    newStartTime: number,
    newTrack?: number,
    snapToFrame: boolean = false,
  ) {
    super(editor, element);

    // Ensure time is never negative and optionally snap to frame
    this.newStartTime = Math.max(
      0,
      snapToFrame ? this.snapTimeToFrame(newStartTime) : newStartTime,
    );
    // Transcript elements should not change track, so we ignore newTrack for them
    this.newTrack = newTrack;
    this.snapToFrame = snapToFrame;

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);

    // Store affected elements for undo
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    let result = false;
    const isTranscriptElem = this.isTranscriptElement();
    if (isTranscriptElem) {
      result = this.shiftTranscriptElement();
    } else {
      result = this.shiftRegularElement();
    }

    if (result) {
      // Recalculate punch list position before global reconciliation so the
      // updated timing is committed ahead of reconcilePunchList.
      const trackManager = this.editor.getTrackManager();
      const isPunchListElement = trackManager.isPunchListElement(
        this.sourceElement.id,
      );
      if (!isTranscriptElem && isPunchListElement) {
        const punchListTrackManager = new PunchListTrackManager(this.editor);
        punchListTrackManager.recalculatePunchListPosition([
          this.sourceElement.id,
        ]);
      }

      // Recalculate timeline duration after successful move (includes reconciliation)
      this.recalculateTimeline();
    }

    return result;
  }

  /**
   * Checks if the source element is a VideoTranscriptElement
   */
  private isTranscriptElement(): boolean {
    return this.sourceElement instanceof VideoTranscriptElement;
  }

  /**
   * Shifts a VideoTranscriptElement using TranscriptTrackManager
   */
  private shiftTranscriptElement(): boolean {
    if (!(this.sourceElement instanceof VideoTranscriptElement)) {
      return false;
    }
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();
    const track = this.editor.getVideoTranscriptTrack();
    const sceneService = new SceneService();
    const prevElement = track?.getPreviousVideoTranscriptElement(
      this.sourceElement.id,
    );
    const nextElement = track?.getNextVideoTranscriptElement(
      this.sourceElement.id,
    );

    const capturedScenes = sceneService.captureOverlappingScenes(
      this.editor.timeline,
      this.sourceElement,
      this.sourceElement.time,
      this.sourceElement.end_time,
      this.sourceElement.getMediaStartTime(),
      this.sourceElement.getMediaEndTime(),
    );

    const shiftResult = transcriptTrackManager.shiftElement(
      this.sourceElement as VideoTranscriptElement,
      this.newStartTime,
      TimelineMode.DEFAULT,
      this.snapToFrame,
      // this.getTimelineMode(), // Pass the mode explicitly TBD: fix ripple shift
    );

    if (
      shiftResult !== false &&
      shiftResult.result === 'cut_paste' &&
      shiftResult.pasted
    ) {
      const anchorTarget = shiftResult.pasted!;
      sceneService.adjustScenesAfterDelete(this.editor.timeline, {
        deletedElementId: this.sourceElement.id,
        previousElementId: prevElement?.id ?? null,
        previousElementMediaEnd: prevElement?.getMediaEndTime() ?? null,
        nextElementId: nextElement?.id ?? null,
        nextElementMediaStart: nextElement?.getMediaStartTime() ?? null,
      });

      capturedScenes.forEach(scene => {
        const newStartAnchor = sceneService.retargetAnchor(
          scene.anchorStart,
          anchorTarget.id,
        );

        const newEndAnchor = sceneService.retargetAnchor(
          scene.anchorEnd,
          anchorTarget.id,
        );

        sceneService.addScene(
          this.editor.timeline,
          new Scene({
            anchorStart: newStartAnchor,
            anchorEnd: newEndAnchor,
            layout: scene.layout,
          }),
        );
      });
    }
    sceneService.validateAndAdjustScenes(this.editor.timeline);
    return shiftResult !== false;
  }

  /**
   * Shifts a regular element using TrackManager
   */
  private shiftRegularElement(): boolean {
    const trackManager = this.editor.getTrackManager();
    return trackManager.shiftElement(
      this.sourceElement,
      this.newStartTime,
      TimelineMode.DEFAULT,
      this.newTrack,
      this.snapToFrame,
      // this.getTimelineMode(), // Pass the mode explicitly
    );
  }

  undo(): boolean {
    // Restore the complete original element state
    this.restoreElementState(this.sourceElement, this.originalState);

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    return `Move element ${this.sourceElement.id} to ${this.newStartTime}s`;
  }
}
